import { Trophy, Crown, Medal, CaretUp, CaretDown, Minus, Sparkle } from '@phosphor-icons/react';
import { Fragment } from 'react';

/**
 * Renderer for TK League rankings (feminino / masculino).
 * Rows sorted by total desc + name tiebreak.
 * Shows variation arrows (vs `previous_position` snapshot) on the podium AND each table row.
 */
export function RankingTable({ rows, genderLabel, crownLabel }) {
  const sum = (arr) => (arr || []).reduce((acc, v) => acc + (v || 0), 0);
  const computeTotal = (p) => sum(p.misto) + sum(p.genero) + sum(p.rei_rainha);

  const tierColor = (position) => {
    if (position === 1) return { bg: '#FFD60A', text: '#1A1A1A', icon: Crown, badge: 'bg-[#FFD60A]' };
    if (position === 2) return { bg: '#C0C0C0', text: '#1A1A1A', icon: Medal, badge: 'bg-[#C0C0C0]' };
    if (position === 3) return { bg: '#CD7F32', text: '#FFFFFF', icon: Medal, badge: 'bg-[#CD7F32]' };
    return null;
  };

  // Sort full table by total desc; name as tiebreak. Podium reuses the same order.
  const players = rows
    .map((p) => ({ ...p, _total: computeTotal(p) }))
    .sort((a, b) => b._total - a._total || (a.name || '').localeCompare(b.name || '', 'pt-BR'));
  const topByTotal = players.slice(0, 3);

  // Variation badge: NEW / UP / DOWN / SAME compared to previous_position snapshot.
  const VariationBadge = ({ currentPos, previousPos, compact = false }) => {
    if (previousPos == null) {
      return (
        <span
          className={`inline-flex items-center gap-0.5 rounded-full bg-[#FFD60A]/20 text-[#7A5400] font-semibold ${compact ? 'px-1.5 py-0 text-[10px]' : 'px-2 py-0.5 text-xs'}`}
          title="Atleta novo nesta etapa"
          data-testid="variation-new"
        >
          <Sparkle size={compact ? 10 : 12} weight="fill" />
          NOVO
        </span>
      );
    }
    const diff = previousPos - currentPos; // positive = moved up
    if (diff > 0) {
      return (
        <span
          className={`inline-flex items-center gap-0.5 rounded-full bg-[#2D6A4F]/15 text-[#2D6A4F] font-semibold ${compact ? 'px-1.5 py-0 text-[10px]' : 'px-2 py-0.5 text-xs'}`}
          title={`Subiu ${diff} posicao${diff > 1 ? 'es' : ''}`}
          data-testid="variation-up"
        >
          <CaretUp size={compact ? 10 : 12} weight="fill" />
          {diff}
        </span>
      );
    }
    if (diff < 0) {
      return (
        <span
          className={`inline-flex items-center gap-0.5 rounded-full bg-[#9B2226]/15 text-[#9B2226] font-semibold ${compact ? 'px-1.5 py-0 text-[10px]' : 'px-2 py-0.5 text-xs'}`}
          title={`Caiu ${Math.abs(diff)} posicao${Math.abs(diff) > 1 ? 'es' : ''}`}
          data-testid="variation-down"
        >
          <CaretDown size={compact ? 10 : 12} weight="fill" />
          {Math.abs(diff)}
        </span>
      );
    }
    return (
      <span
        className={`inline-flex items-center rounded-full bg-[#73706A]/15 text-[#73706A] font-semibold ${compact ? 'px-1 py-0' : 'px-1.5 py-0.5'}`}
        title="Mesma posicao da etapa anterior"
        data-testid="variation-same"
      >
        <Minus size={compact ? 10 : 12} weight="bold" />
      </span>
    );
  };

  const hasAnyPrev = players.some((p) => p.previous_position != null);

  return (
    <div className="space-y-4" data-testid={`ranking-${genderLabel.toLowerCase()}`}>
      <div className="bg-white border border-[#E6E2D8] rounded-xl overflow-hidden shadow-sm">
        <div className="bg-gradient-to-r from-[#1A1A1A] to-[#E85D04] p-4 text-white">
          <div className="flex items-center gap-2">
            <Trophy size={22} weight="fill" className="text-[#FFB703]" />
            <h3 className="font-['Outfit'] text-lg font-bold">Ranking Geral - {genderLabel}</h3>
          </div>
          <p className="text-xs text-white/80 mt-1">{players.length} participantes{hasAnyPrev ? ' - variacao vs etapa anterior' : ''}</p>
        </div>

        {/* Podium top 3 (by total) */}
        {topByTotal.length >= 3 && (
          <div className="grid grid-cols-3 gap-3 p-4 bg-[#FDFBF7] border-b border-[#E6E2D8]" data-testid="ranking-podium">
            {[topByTotal[1], topByTotal[0], topByTotal[2]].map((player, idx) => {
              const realPos = idx === 0 ? 2 : idx === 1 ? 1 : 3;
              const tier = tierColor(realPos);
              const heights = { 1: 'h-28', 2: 'h-20', 3: 'h-16' };
              return (
                <div key={player.name} className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${tier.badge}`} style={{ color: tier.text }}>
                    <tier.icon size={18} weight="fill" />
                  </div>
                  <p className="text-sm font-semibold text-[#1A1A1A] text-center truncate max-w-full px-1">{player.name}</p>
                  <div className="flex items-center gap-1 mb-1">
                    <p className="text-xs text-[#73706A]">{player._total} pts</p>
                    {hasAnyPrev && (
                      <VariationBadge currentPos={realPos} previousPos={player.previous_position} compact />
                    )}
                  </div>
                  <div className={`w-full ${heights[realPos]} rounded-t-lg flex items-end justify-center pb-2`} style={{ backgroundColor: tier.bg }}>
                    <span className="font-['Outfit'] font-bold text-xl" style={{ color: tier.text }}>{realPos}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Full table - same 2-row header layout as the spreadsheet */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#F5F2EB] text-[#73706A] uppercase">
              <tr>
                <th rowSpan={2} className="px-2 py-2 text-left whitespace-nowrap align-middle border-r border-[#E6E2D8]">#</th>
                <th rowSpan={2} className="px-2 py-2 text-left min-w-[120px] align-middle border-r border-[#E6E2D8]">Atleta</th>
                {[1, 2, 3, 4, 5].map(n => (
                  <th key={`e${n}`} colSpan={3} className="px-2 py-2 text-center border-r border-[#E6E2D8] bg-[#EEE9DC] font-bold">E{n}</th>
                ))}
                <th rowSpan={2} className="px-2 py-2 text-right align-middle">Total</th>
              </tr>
              <tr>
                {[1, 2, 3, 4, 5].map(n => (
                  <Fragment key={`sub${n}`}>
                    <th className="px-2 py-1 text-center font-medium whitespace-nowrap">Misto</th>
                    <th className="px-2 py-1 text-center font-medium whitespace-nowrap">{genderLabel}</th>
                    <th className="px-2 py-1 text-center font-medium whitespace-nowrap border-r border-[#E6E2D8]">{crownLabel}</th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {players.map((player, i) => {
                const position = i + 1;
                const isTopByTotal = topByTotal.findIndex(t => t.name === player.name) >= 0;
                return (
                  <tr
                    key={`${player.name}-${i}`}
                    className={`border-t border-[#E6E2D8] hover:bg-[#FDFBF7] transition-colors ${isTopByTotal ? 'font-medium' : ''}`}
                    data-testid={`ranking-row-${position}`}
                  >
                    <td className="px-2 py-2 text-left text-[#73706A] border-r border-[#E6E2D8] whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span>{position}</span>
                        {hasAnyPrev && (
                          <VariationBadge currentPos={position} previousPos={player.previous_position} compact />
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-[#1A1A1A] text-left border-r border-[#E6E2D8]">{player.name}</td>
                    {[0, 1, 2, 3, 4].map(ei => (
                      <Fragment key={`e${ei}`}>
                        <td className="px-2 py-2 text-center text-[#73706A]">{player.misto?.[ei] != null ? player.misto[ei] : '-'}</td>
                        <td className="px-2 py-2 text-center text-[#73706A]">{player.genero?.[ei] != null ? player.genero[ei] : '-'}</td>
                        <td className="px-2 py-2 text-center text-[#73706A] border-r border-[#E6E2D8]">{player.rei_rainha?.[ei] != null ? player.rei_rainha[ei] : '-'}</td>
                      </Fragment>
                    ))}
                    <td className="px-2 py-2 text-right font-['Outfit'] font-bold text-[#E85D04]">{player._total}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-[#73706A] text-center px-4">
        Atletas ordenados por total de pontos (maior para menor). {hasAnyPrev ? 'As setas mostram a variacao em relacao a etapa anterior.' : 'Apos a proxima etapa, as setas de variacao aparecerao automaticamente.'}
      </p>
    </div>
  );
}
